import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, Edit2, ChevronDown, Search, Menu, HelpCircle, HomeIcon, Users, ClipboardList, Eye, Shield, MessageSquare, X, Plus, HeartPulse, CreditCard, Info, MapPin } from 'lucide-react';
export function DecisionAction({
  onComplete
}: {
  onComplete?: () => void;
}) {
  const [phase, setPhase] = useState(1);
  const [creditLimitText, setCreditLimitText] = useState('50,000');
  const [paymentTermsText, setPaymentTermsText] = useState('30');
  const [reasoningText, setReasoningText] = useState('');
  useEffect(() => {
    const sequence = [{
      phase: 1,
      delay: 3500
    }, {
      phase: 2,
      delay: 6000
    }, {
      phase: 3,
      delay: 7500
    }, {
      phase: 4,
      delay: 8500
    }, {
      phase: 5,
      delay: 9500
    }, {
      phase: 6,
      delay: 14000
    }, {
      phase: 7,
      delay: 15500
    }, {
      phase: 8,
      delay: 16500
    }, {
      phase: 9,
      delay: 18000
    } // Notes sidebar opens
    ];
    const timeouts = sequence.map(({
      phase,
      delay
    }) => setTimeout(() => setPhase(phase), delay));
    const completeTimer = setTimeout(() => {
      if (onComplete) onComplete();
    }, 24000);
    return () => {
      timeouts.forEach(clearTimeout);
      clearTimeout(completeTimer);
    };
  }, [onComplete]);
  useEffect(() => {
    if (phase === 5) {
      // Clear initial values
      setCreditLimitText('');
      setPaymentTermsText('');
      let clText = '35,000';
      let ptText = '45';
      let rText = 'Reducing initial limit to $35,000 due to limited payment history. Will review for increase after 6 months of on-time payments.';
      let clIndex = 0;
      let ptIndex = 0;
      let rIndex = 0;
      const clInterval = setInterval(() => {
        if (clIndex <= clText.length) {
          setCreditLimitText(clText.slice(0, clIndex));
          clIndex++;
        } else {
          clearInterval(clInterval);
          const ptInterval = setInterval(() => {
            if (ptIndex <= ptText.length) {
              setPaymentTermsText(ptText.slice(0, ptIndex));
              ptIndex++;
            } else {
              clearInterval(ptInterval);
              const rInterval = setInterval(() => {
                if (rIndex <= rText.length) {
                  setReasoningText(rText.slice(0, rIndex));
                  rIndex++;
                } else {
                  clearInterval(rInterval);
                }
              }, 15); // Faster typing for long text
            }
          }, 50);
        }
      }, 50);
      return () => clearInterval(clInterval);
    } else if (phase < 5) {
      setCreditLimitText('50,000');
      setPaymentTermsText('30');
      setReasoningText('');
    }
  }, [phase]);
  const isExpanded = phase >= 1;
  const isModalOpen = phase >= 4 && phase < 8;
  const isNotesOpen = phase >= 9;
  return <div className="w-full h-full bg-white rounded-xl overflow-hidden flex flex-col text-sm border border-gray-200/50 relative" data-id="element-366">
      {/* Product Header */}
      <div className="flex items-center gap-3 px-4 py-1.5 border-b border-gray-200 bg-white shrink-0 z-10 relative" data-id="element-367">
        <Menu className="w-4 h-4 text-gray-500" data-id="element-368" />
        <img src="https://cdn.magicpatterns.com/uploads/a5S8ZQgbN9jzaBrUDmr99x/Logo_CreditPulse_Black.png" alt="Credit Pulse" className="h-5 object-contain" data-id="element-369" />
        <div className="flex-1 mx-3 relative" data-id="element-370">
          <input type="text" placeholder="Search Companies" className="w-full pl-3 pr-8 py-1 text-xs border border-gray-200 rounded bg-gray-50 focus:outline-none" data-id="element-371" />
          <Search className="w-3 h-3 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2" data-id="element-372" />
        </div>
        <HelpCircle className="w-4 h-4 text-gray-400" data-id="element-373" />
        <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center text-[8px] font-bold text-white" data-id="element-374">
          SU
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden relative" data-id="element-375">
        {/* Left Sidebar */}
        <div className="w-10 bg-white border-r border-gray-200 flex flex-col items-center py-3 gap-3.5 shrink-0 z-10 relative" data-id="element-376">
          <HomeIcon className="w-4 h-4 text-gray-400" data-id="element-377" />
          <Users className="w-4 h-4 text-gray-400" data-id="element-378" />
          <ClipboardList className="w-4 h-4 text-gray-400" data-id="element-379" />
          <div className="w-7 h-7 rounded-md bg-[#6C5CE7]/10 flex items-center justify-center" data-id="element-380">
            <Eye className="w-4 h-4 text-[#6C5CE7]" data-id="element-381" />
          </div>
          <Shield className="w-4 h-4 text-gray-400" data-id="element-382" />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden relative" data-id="element-383">
          {/* Company Header */}
          <div className="px-5 py-2 border-b border-gray-200 shrink-0" data-id="element-384">
            <div className="flex items-center mb-1.5" data-id="element-385">
              <div className="flex-1 min-w-0" data-id="element-386">
                <h2 className="text-base font-bold text-gray-900 leading-tight" data-id="element-387">
                  DUNDER MIFFLIN PAPER COMPANY
                </h2>
                <p className="text-[11px] text-gray-500 mt-0.5" data-id="element-388">
                  Credit Manager: Dave Davidson
                </p>
              </div>
              <div className="flex flex-col items-center shrink-0 mx-auto" data-id="element-389">
                <div className="flex items-center gap-1.5 mb-0.5" data-id="element-390">
                  <span className="text-[10px] font-medium text-gray-500" data-id="element-391">
                    Pulse Score
                  </span>
                  <span className="text-sm font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded" data-id="element-392">
                    82
                  </span>
                </div>
                <div className="w-32 h-1.5 bg-gray-200 rounded-full overflow-hidden flex" data-id="element-393">
                  <div className="h-full bg-red-400 w-1/3" data-id="element-394" />
                  <div className="h-full bg-amber-400 w-1/3" data-id="element-395" />
                  <div className="h-full bg-green-500 w-1/3 relative" data-id="element-396">
                    <div className="absolute top-0 bottom-0 w-0.5 bg-gray-900 left-[60%]" data-id="element-397" />
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-center gap-1.5 shrink-0" data-id="element-398">
                <span className="flex items-center gap-1 px-2.5 py-1 bg-gray-100 border border-gray-200 rounded-full text-[10px] font-medium text-gray-600" data-id="element-399">
                  <MapPin className="w-3 h-3" data-id="element-400" /> Headquarters
                </span>
                <button className="w-7 h-7 rounded-lg bg-[#6C5CE7]/10 flex items-center justify-center hover:bg-[#6C5CE7]/20 transition-colors" data-id="element-401">
                  <MessageSquare className="w-3.5 h-3.5 text-[#6C5CE7]" data-id="element-402" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-6 border-b border-gray-200 justify-center" data-id="element-403">
              {['Overview', 'Notifications', 'Details', 'Financials', 'People', 'Applications'].map(tab => <button key={tab} className={`pb-2 text-[10px] font-medium uppercase tracking-wider relative
                    ${tab === 'Overview' ? 'text-[#6C5CE7]' : 'text-gray-500'}`} data-id="element-404">
                  {tab}
                  {tab === 'Overview' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#6C5CE7]" data-id="element-405"></div>}
                </button>)}
            </div>
          </div>

          <div className="flex-1 overflow-hidden p-5 bg-gray-50/30 relative" data-id="element-406">
            <div className="flex flex-col" data-id="element-407">
              {/* Decision Banner */}
              <motion.div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-4 shrink-0 shadow-sm" animate={{
              height: isExpanded ? 'auto' : 46
            }} transition={{
              duration: 0.4,
              ease: 'easeInOut'
            }} data-id="element-408">
                <div className="p-3 flex items-center justify-between cursor-pointer" data-id="element-409">
                  <div className="flex items-center gap-3 flex-wrap" data-id="element-410">
                    <span className="font-bold text-gray-900 text-sm" data-id="element-411">
                      Credit Recommendation
                    </span>
                    <span className="px-2 py-0.5 bg-white text-green-600 text-[10px] font-medium rounded-full border border-green-400" data-id="element-412">
                      Ready
                    </span>
                    <span className="text-gray-500 text-xs" data-id="element-413">
                      Date: March 27, 2026
                    </span>
                    <span className="text-gray-300" data-id="element-414">·</span>
                    <span className="font-medium text-gray-700 text-xs" data-id="element-415">
                      Recommended Credit Limit: $50,000
                    </span>
                  </div>
                  <motion.div animate={{
                  rotate: isExpanded ? 180 : 0
                }} transition={{
                  duration: 0.3
                }} data-id="element-416">
                    <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" data-id="element-417" />
                  </motion.div>
                </div>

                <AnimatePresence data-id="element-418">
                  {isExpanded && <motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.3,
                  delay: 0.1
                }} className="border-t border-gray-100" data-id="element-419">
                      <div className="flex" data-id="element-420">
                        {/* Left Column — tinted background */}
                        <div className="w-2/5 bg-slate-50/80 p-5 border-r border-gray-100" data-id="element-421">
                          <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1" data-id="element-422">
                            Recommended Decision
                          </p>
                          <h3 className="text-base font-bold text-gray-900 mb-3" data-id="element-423">
                            Approve for $50,000
                          </h3>
                          <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1" data-id="element-424">
                            Terms
                          </p>
                          <div className="space-y-0.5 text-xs text-gray-600" data-id="element-425">
                            <p data-id="element-426">Payment Terms: Net 30</p>
                            <p data-id="element-427">Discount: —</p>
                            <p data-id="element-428">Late Pay Penalty: —</p>
                          </div>
                        </div>

                        {/* Right Column */}
                        <div className="flex-1 p-5 space-y-2.5" data-id="element-429">
                          <div data-id="element-430">
                            <p className="text-[10px] text-gray-400 mb-0.5" data-id="element-431">
                              Policy Applied ·{' '}
                              <span className="text-[#6C5CE7] cursor-pointer" data-id="element-432">
                                (View Policy)
                              </span>
                            </p>
                            <p className="text-sm font-bold text-gray-900" data-id="element-433">
                              Company Credit Policy V1
                            </p>
                          </div>
                          <div data-id="element-434">
                            <p className="text-[10px] text-gray-400 mb-0.5" data-id="element-435">
                              Credit Application ·{' '}
                              <span className="text-[#6C5CE7] cursor-pointer" data-id="element-436">
                                (View Application)
                              </span>
                            </p>
                            <p className="text-xs text-gray-600" data-id="element-437">
                              Submitted on: March 27, 2026
                            </p>
                          </div>
                          <div data-id="element-438">
                            <p className="text-[10px] text-gray-400 mb-0.5" data-id="element-439">
                              Recommendation Summary ·{' '}
                              <span className="text-[#6C5CE7] cursor-pointer" data-id="element-440">
                                (Full Reasoning)
                              </span>
                            </p>
                            <p className="text-[11px] text-gray-600 leading-relaxed" data-id="element-441">
                              Approved based on strong fundamentals, 77-year
                              operating history, and positive trade references.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="grid grid-cols-4 gap-2 px-5 pb-4 pt-3 border-t border-gray-100 bg-gray-50/50" data-id="element-442">
                        <button className="py-2.5 bg-green-700 text-white text-[10px] font-bold rounded text-center uppercase tracking-wider" data-id="element-443">
                          Approve
                        </button>
                        <motion.button animate={phase === 3 ? {
                      scale: 0.9,
                      backgroundColor: '#7a5f00',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                    } : phase === 2 ? {
                      scale: [1, 1.08, 1],
                      backgroundColor: '#b47c00',
                      boxShadow: ['0 2px 4px rgba(180,124,0,0.2)', '0 6px 20px rgba(180,124,0,0.5)', '0 2px 4px rgba(180,124,0,0.2)']
                    } : {
                      scale: 1,
                      backgroundColor: '#b47c00',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                    }} transition={phase === 2 ? {
                      duration: 0.8,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    } : {
                      duration: 0.15
                    }} className="py-2.5 bg-[#b47c00] text-white text-[10px] font-bold rounded uppercase tracking-wider" data-id="element-444">
                          Approve with Changes
                        </motion.button>
                        <button className="py-2.5 bg-[#dc2626] text-white text-[10px] font-bold rounded text-center uppercase tracking-wider" data-id="element-445">
                          Reject
                        </button>
                        <button className="py-2.5 bg-white text-[#6C5CE7] text-[10px] font-bold rounded text-center border border-gray-200 uppercase tracking-wider" data-id="element-446">
                          Regenerate
                        </button>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>

              {/* Score Cards */}
              <div className="grid grid-cols-3 gap-3" data-id="element-447">
                {[{
                label: 'Pulse Score',
                value: 78,
                color: 'green',
                border: 'border-l-green-500'
              }, {
                label: 'Health Score',
                value: 65,
                color: 'amber',
                border: 'border-l-amber-400'
              }, {
                label: 'Payment Score',
                value: 82,
                color: 'green',
                border: 'border-l-green-500'
              }].map((score, i) => <motion.div key={score.label} className={`bg-white border border-gray-200 border-l-2 ${score.border} rounded-lg p-3 shadow-sm`} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.5,
                delay: 0.1 + i * 0.15,
                ease: 'easeOut'
              }} data-id="element-448">
                    <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-449">
                      {score.label}
                    </p>
                    <div className="flex items-center gap-2" data-id="element-450">
                      <span className="text-xl font-bold text-gray-900 leading-none" data-id="element-451">
                        {score.value}
                      </span>
                      <div className={`flex-1 h-1.5 rounded-full ${score.color === 'green' ? 'bg-gray-100' : 'bg-gray-100'} overflow-hidden`} data-id="element-452">
                        <div className={`h-full rounded-full ${score.color === 'green' ? 'bg-green-500' : 'bg-amber-400'}`} style={{
                      width: `${score.value}%`
                    }} data-id="element-453" />
                      </div>
                    </div>
                  </motion.div>)}
              </div>

              {/* Score Trend Chart — hidden when banner expands */}
              <AnimatePresence data-id="element-454">
                {!isExpanded && <motion.div className="bg-white border border-gray-200 rounded-lg p-4 pb-6 shadow-sm mt-5" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                height: 0,
                marginTop: 0,
                padding: 0,
                overflow: 'hidden'
              }} transition={{
                duration: 0.4,
                delay: 0.6,
                ease: 'easeOut'
              }} data-id="element-455">
                    <div className="flex items-center justify-between mb-4" data-id="element-456">
                      <h3 className="text-xs font-bold text-gray-900" data-id="element-457">
                        Score Trend — Last 12 Months
                      </h3>
                      <div className="flex items-center gap-3" data-id="element-458">
                        {[{
                      label: 'Pulse',
                      color: 'bg-[#6C5CE7]'
                    }, {
                      label: 'Health',
                      color: 'bg-amber-400'
                    }, {
                      label: 'Payment',
                      color: 'bg-green-500'
                    }].map(legend => <div key={legend.label} className="flex items-center gap-1" data-id="element-459">
                            <div className={`w-2 h-2 rounded-full ${legend.color}`} data-id="element-460" />
                            <span className="text-[9px] text-gray-500" data-id="element-461">
                              {legend.label}
                            </span>
                          </div>)}
                      </div>
                    </div>
                    <div className="relative h-[90px]" data-id="element-462">
                      {[0, 25, 50, 75, 100].map(val => <div key={val} className="absolute left-0 right-0 border-t border-gray-100" style={{
                    bottom: `${val}%`
                  }} data-id="element-463" />)}
                      <svg width="100%" height="100%" viewBox="0 0 300 100" preserveAspectRatio="none" className="overflow-visible relative z-10" data-id="element-464">
                        <polyline points="0,40 27,42 54,38 81,35 108,30 135,28 162,25 189,22 216,24 243,22 270,22 300,22" fill="none" stroke="#6C5CE7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-465" />
                        <polyline points="0,55 27,50 54,52 81,48 108,45 135,42 162,40 189,38 216,36 243,35 270,35 300,35" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-466" />
                        <polyline points="0,30 27,32 54,28 81,25 108,22 135,20 162,22 189,18 216,20 243,18 270,18 300,18" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-467" />
                        <circle cx="300" cy="22" r="3.5" fill="#6C5CE7" stroke="white" strokeWidth="1.5" data-id="element-468" />
                        <circle cx="300" cy="35" r="3.5" fill="#f59e0b" stroke="white" strokeWidth="1.5" data-id="element-469" />
                        <circle cx="300" cy="18" r="3.5" fill="#22c55e" stroke="white" strokeWidth="1.5" data-id="element-470" />
                      </svg>
                      <div className="absolute bottom-[-18px] left-0 right-0 flex justify-between" data-id="element-471">
                        {['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'].map(m => <span key={m} className="text-[8px] text-gray-400" data-id="element-472">
                            {m}
                          </span>)}
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Modal Overlay */}
        <AnimatePresence data-id="element-473">
          {isModalOpen && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 bg-gray-900/20 backdrop-blur-sm z-40 flex items-center justify-center" data-id="element-474">
              <motion.div initial={{
            scale: 0.95,
            opacity: 0,
            y: 20
          }} animate={{
            scale: 1,
            opacity: 1,
            y: 0
          }} exit={{
            scale: 0.95,
            opacity: 0,
            y: 20
          }} className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden" data-id="element-475">
                <div className="px-6 py-4 border-b border-gray-100" data-id="element-476">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-477">
                    Approve with Changes
                  </h2>
                </div>

                <div className="p-6 space-y-5" data-id="element-478">
                  <div className="grid grid-cols-3 gap-4" data-id="element-479">
                    <div className="col-span-2 relative" data-id="element-480">
                      <label className="absolute -top-2 left-3 bg-white px-1 text-[10px] text-gray-500" data-id="element-481">
                        Credit Limit
                      </label>
                      <input type="text" value={creditLimitText} readOnly className={`w-full border rounded p-2.5 text-sm focus:outline-none ${phase >= 5 && creditLimitText !== '50,000' ? 'border-[#b47c00] text-[#b47c00] font-medium bg-amber-50/30' : 'border-gray-200 text-gray-900'}`} data-id="element-482" />
                    </div>
                    <div className="relative" data-id="element-483">
                      <label className="absolute -top-2 left-3 bg-white px-1 text-[10px] text-gray-500" data-id="element-484">
                        Currency
                      </label>
                      <div className="w-full border border-gray-200 rounded p-2.5 text-sm text-gray-900 flex justify-between items-center" data-id="element-485">
                        <span data-id="element-486">USD</span>
                        <ChevronDown className="w-4 h-4 text-gray-400" data-id="element-487" />
                      </div>
                    </div>
                  </div>

                  <div className="relative" data-id="element-488">
                    <label className="absolute -top-2 left-3 bg-white px-1 text-[10px] text-gray-500" data-id="element-489">
                      Payment Terms Days
                    </label>
                    <input type="text" value={paymentTermsText} readOnly className={`w-full border rounded p-2.5 text-sm focus:outline-none ${phase >= 5 && paymentTermsText !== '30' ? 'border-[#b47c00] text-[#b47c00] font-medium bg-amber-50/30' : 'border-gray-200 text-gray-900'}`} data-id="element-490" />
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-491">
                    <div className="relative" data-id="element-492">
                      <label className="absolute -top-2 left-3 bg-white px-1 text-[10px] text-gray-500" data-id="element-493">
                        Discount (%)
                      </label>
                      <input type="text" className="w-full border border-gray-200 rounded p-2.5 text-sm text-gray-900 focus:outline-none" data-id="element-494" />
                    </div>
                    <div className="relative" data-id="element-495">
                      <label className="absolute -top-2 left-3 bg-white px-1 text-[10px] text-gray-500" data-id="element-496">
                        Discount Days
                      </label>
                      <input type="text" className="w-full border border-gray-200 rounded p-2.5 text-sm text-gray-900 focus:outline-none" data-id="element-497" />
                    </div>
                  </div>

                  <div className="relative" data-id="element-498">
                    <label className="absolute -top-2 left-3 bg-white px-1 text-[10px] text-gray-500" data-id="element-499">
                      Reasoning for Changes
                    </label>
                    <textarea rows={3} value={reasoningText} readOnly className={`w-full border rounded p-2.5 text-sm focus:outline-none resize-none ${phase >= 5 && reasoningText.length > 0 ? 'border-[#b47c00] text-[#b47c00] bg-amber-50/30' : 'border-gray-200 text-gray-900'}`} data-id="element-500"></textarea>
                  </div>
                </div>

                <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50/50" data-id="element-501">
                  <button className="px-4 py-2 text-sm font-bold text-gray-500" data-id="element-502">
                    CANCEL
                  </button>
                  <motion.button animate={phase === 7 ? {
                scale: 0.9,
                backgroundColor: '#7a5f00',
                boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
              } : phase === 6 ? {
                scale: [1, 1.08, 1],
                backgroundColor: '#b47c00',
                boxShadow: ['0 2px 4px rgba(180,124,0,0.2)', '0 6px 20px rgba(180,124,0,0.5)', '0 2px 4px rgba(180,124,0,0.2)']
              } : {
                scale: 1,
                backgroundColor: '#b47c00',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }} transition={phase === 6 ? {
                duration: 0.8,
                repeat: Infinity,
                ease: 'easeInOut'
              } : {
                duration: 0.15
              }} className="px-6 py-2 bg-[#b47c00] text-white text-sm font-bold rounded" data-id="element-503">
                    SAVE & APPROVE
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>}
        </AnimatePresence>

        {/* Notes Sidebar */}
        <AnimatePresence data-id="element-504">
          {isNotesOpen && <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="absolute top-0 right-0 bottom-0 w-[340px] bg-white border-l border-gray-200 shadow-2xl z-30 flex flex-col" data-id="element-505">
              <div className="px-4 py-3 border-b border-gray-200 flex items-start justify-between shrink-0" data-id="element-506">
                <div className="flex items-start gap-3" data-id="element-507">
                  <MessageSquare className="w-5 h-5 text-gray-700 mt-0.5" data-id="element-508" />
                  <div data-id="element-509">
                    <h2 className="text-base font-bold text-gray-900" data-id="element-510">Notes</h2>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mt-0.5" data-id="element-511">
                      DUNDER MIFFLIN PAPER COMPANY
                    </p>
                  </div>
                </div>
                <button className="text-gray-400 hover:text-gray-600" data-id="element-512">
                  <X className="w-5 h-5" data-id="element-513" />
                </button>
              </div>

              <div className="p-4 flex-1 overflow-hidden flex flex-col" data-id="element-514">
                {/* Add Note Button */}
                <button className="w-full mb-3 px-4 py-2.5 bg-[#6C5CE7] text-white text-xs font-bold rounded flex items-center justify-center gap-2 hover:bg-[#5a4cdb] transition-colors" data-id="element-515">
                  <Plus className="w-4 h-4" data-id="element-516" /> ADD NOTE
                </button>

                {/* All Notes Dropdown */}
                <div className="mb-4 relative" data-id="element-517">
                  <select className="w-full px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded appearance-none cursor-pointer hover:border-gray-400 transition-colors" data-id="element-518">
                    <option data-id="element-519">All Notes</option>
                  </select>
                  <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-520" />
                </div>

                {/* User Note */}
                <div className="mb-4 p-3 bg-[#6C5CE7]/[0.04] border border-[#6C5CE7]/15 rounded-lg" data-id="element-521">
                  <div className="flex items-center gap-2 mb-1.5" data-id="element-522">
                    <div className="w-5 h-5 rounded-full bg-[#6C5CE7] flex items-center justify-center text-[8px] font-bold text-white" data-id="element-523">
                      DD
                    </div>
                    <span className="text-xs font-bold text-gray-900" data-id="element-524">
                      Dave Davidson
                    </span>
                    <span className="text-[9px] text-gray-400 ml-auto" data-id="element-525">
                      Just now
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-700 leading-relaxed" data-id="element-526">
                    Spoke with Michael Scott — they're expecting a large Q2
                    order from a new client. Will revisit limit increase in 6
                    months if payment history is clean.
                  </p>
                </div>

                <div className="space-y-4 relative" data-id="element-527">
                  <div className="absolute left-[4px] top-2 bottom-0 w-0.5 bg-gray-100" data-id="element-528"></div>

                  {/* Timeline Item 1 */}
                  <div className="relative pl-6" data-id="element-529">
                    <div className="absolute left-0 top-1.5 w-2.5 h-2.5 bg-[#6C5CE7] rounded-full border-2 border-white shadow-[0_0_6px_rgba(108,92,231,0.4)]" data-id="element-530"></div>
                    <p className="text-[10px] text-gray-400 mb-0.5" data-id="element-531">
                      March 27, 2026 · 10:17 AM
                    </p>
                    <h4 className="text-xs font-bold text-gray-900 mb-0.5" data-id="element-532">
                      Credit Approved with Changes
                    </h4>
                    <p className="text-[11px] text-gray-600 leading-relaxed" data-id="element-533">
                      Credit limit set to $35,000 with 45-day terms.
                    </p>
                  </div>

                  {/* Timeline Item 2 */}
                  <div className="relative pl-6" data-id="element-534">
                    <div className="absolute left-0 top-1.5 w-2.5 h-2.5 bg-gray-300 rounded-full border-2 border-white" data-id="element-535"></div>
                    <p className="text-[10px] text-gray-400 mb-0.5" data-id="element-536">
                      March 27, 2026 · 10:15 AM
                    </p>
                    <h4 className="text-xs font-bold text-gray-900 mb-0.5" data-id="element-537">
                      Application Submitted
                    </h4>
                    <p className="text-[11px] text-gray-600 leading-relaxed" data-id="element-538">
                      Submitted by Pam Beesly on behalf of Dunder Mifflin.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}